/**
 * Permanent (301) redirects for URLs that no longer exist (TICKET-10387).
 *
 * Single source of truth for:
 *   - nuxt.config.ts  -> nitro.routeRules (dev / preview / node server only)
 *   - render.yaml     -> `routes:` redirects
 *
 * PRODUCTION: unified.to is a static site managed in the Render dashboard (service
 * "Unified-www4"), which does not read render.yaml, and routeRules redirects don't exist
 * in a static `nuxt generate` build. A redirect only goes live once it is added under
 * Render dashboard -> Unified-www4 -> Redirects/Rewrites. After editing this list, add the new
 * rules with `RENDER_API_KEY=... npm run syncredirects -- --apply` (server/utils/sync-render-redirects.ts).
 *
 * Each entry must land on a URL that returns 200 in a single hop. Never point a
 * retired page at an unrelated page (e.g. the homepage): Google treats that as a
 * soft 404. Keep render.yaml in sync by hand when editing this list.
 */
export interface ISiteRedirect {
    from: string;
    to: string;
}

const MERGE_VS_UNIFIED = '/blog/merge_vs_unified_which_unified_api_should_you_choose_in_2026';
const FUNDING_POST = '/blog/unified_secures_dollar1_1m_pre_seed_to_power_integrations_for_ai_first_companies';

export const SITE_REDIRECTS: readonly ISiteRedirect[] = [
    // Blog posts that moved or were renamed.
    {
        from: '/blog/how_virtual_webhook_retry_and_recovery_works',
        to: 'https://docs.unified.to/guides/how_virtual_webhook_retry_and_recovery_works',
    },
    {
        from: '/blog/workable_creating_candidates_and_attaching_them_to_jobs',
        to: 'https://docs.unified.to/guides/workable_creating_candidates_and_attaching_them_to_jobs',
    },
    {
        from: '/blog/replacing_polling_with_unified_webhooks_and_virtual_streams',
        to: '/blog/replacing_polling_with_event_driven_integrations_using_virtual_webhooks',
    },
    { from: '/blog/merge_vs_unified_which_unified_api_is_right_for_your_product', to: MERGE_VS_UNIFIED },
    { from: '/blog/merge_vs_unified_a_2026_comparison_for_saas_and_ai_teams', to: MERGE_VS_UNIFIED },
    // The `$` in the old funding-post slug broke the blog index link; the post is now
    // published at the `dollar` slug (see getName / safeFilename in server/utils/helpcenter.ts).
    {
        from: '/blog/unified_secures_$1_1m_pre_seed_to_power_integrations_for_ai_first_companies',
        to: FUNDING_POST,
    },
    {
        from: '/blog/unified_secures_%241_1m_pre_seed_to_power_integrations_for_ai_first_companies',
        to: FUNDING_POST,
    },

    // Blog category cluster pass (TICKET-11152): posts merged or renamed on 12 Oct 2026.
    // Row 5 of the ticket (hidden HR & Directory post) is not listed: its published slug comes from
    // the Notion `filename` field and must be added once confirmed.
    {
        from: '/blog/unified_lms_api_sync_courses_enrollments_and_learning_data_across_platforms',
        to: '/blog/learning_management_system_lms_api_integration_real_time_course_data_and_learning_platforms',
    },
    {
        from: '/blog/ats_unified_apis_what_to_look_for_use_cases_and_options',
        to: '/blog/best_unified_api_for_ats_integrations_in_2026',
    },
    {
        from: '/blog/calendar_unified_apis_features_use_cases_and_how_to_choose_the_right_one',
        to: '/blog/best_unified_api_for_calendar_and_meeting_integrations_in_2026',
    },
    {
        from: '/blog/announcing_the_calendar_api_access_availability_events_and_scheduling',
        to: '/blog/calendar_api_integration_real_time_events_free_busy_scheduling_links_and_meeting_recordings_across_platforms',
    },
    {
        from: '/blog/what_unified_api_is_best_for_calendar_and_meeting_platform_integrations',
        to: '/blog/best_unified_api_for_calendar_and_meeting_integrations_in_2026',
    },
    {
        from: '/blog/best_unified_api_for_shipping_and_logistics_integrations_in_2026',
        to: '/blog/what_is_the_best_unified_api_for_shipping_and_logistics_integrations_in_2026',
    },
    {
        from: '/blog/what_unified_api_is_best_for_shipping_and_logistics_integrations_for_e_commerce_platforms_in_2026',
        to: '/blog/what_is_the_best_unified_api_for_shipping_and_logistics_integrations_in_2026',
    },

    // Renamed Merge Agent Handler comparison post (TICKET-10883).
    {
        from: '/blog/unified_mcp_vs_merge_agent_handler_a_2026_comparison',
        to: '/blog/unified_mcp_vs_merge_agent_handler_which_mcp_server_should_your_agent_use_2026',
    },

    // Renamed Kombo comparison post (TICKET-10886).
    {
        from: '/blog/kombo_vs_unified_sync_and_store_vs_real_time_pass_through_2026',
        to: '/blog/kombo_vs_unified_sync_and_store_vs_live_source_reads_2026',
    },

    // Duplicate payments post -> canonical post (TICKET-10904).
    {
        from: '/blog/payment_api_integration_transactions_refunds_payouts_and_subscriptions_across_providers',
        to: '/blog/what_is_a_unified_payments_api_orchestration_processing_and_data_layers_explained',
    },

    // Merged Arcade.dev and Composio comparison posts (TICKET-10905).
    {
        from: '/blog/arcade_dev_vs_unified_which_integration_platform_powers_ai_driven_saas_better',
        to: '/blog/arcade_vs_unified_which_mcp_platform_should_your_ai_agents_use_in_2026',
    },
    {
        from: '/blog/unified_mcp_vs_arcade_dev_a_2026_comparison',
        to: '/blog/arcade_vs_unified_which_mcp_platform_should_your_ai_agents_use_in_2026',
    },
    {
        from: '/blog/unified_mcp_vs_arcade_dev_mcp_comparison_which_is_better_for_ai_agents_2026',
        to: '/blog/arcade_vs_unified_which_mcp_platform_should_your_ai_agents_use_in_2026',
    },
    {
        from: '/blog/unified_mcp_vs_composio_mcp_a_2026_comparison',
        to: '/blog/composio_vs_unified_which_mcp_platform_is_right_for_your_ai_product_in_2026',
    },
    {
        from: '/blog/unified_mcp_vs_composio_mcp_comparison_which_is_better_for_ai_agents',
        to: '/blog/composio_vs_unified_which_mcp_platform_is_right_for_your_ai_product_in_2026',
    },

    // Webhook content cluster (TICKET-10395).
    {
        from: '/blog/best_practices_for_zero_storage_webhooks_and_data_residency_controls_in_regulated_saas_applications_1',
        to: '/blog/best_practices_for_zero_storage_webhooks_and_data_residency_controls_in_regulated_saas_applications',
    },
    {
        from: '/blog/which_unified_api_platform_supports_real_time_webhooks',
        to: '/blog/which_unified_api_platforms_support_virtual_webhooks_vs_sync_based_notifications',
    },
    {
        from: '/blog/introducing_powerful_enhancements_to_webhooks',
        to: '/blog/replace_polling_with_unified_webhooks_real_time_and_historical_data_in_one_flow',
    },
    {
        from: '/blog/which_event_delivery_model_scales_polling_webhooks_or_virtual_webhooks',
        to: '/blog/polling_vs_webhooks_when_to_use_one_over_the_other',
    },
    {
        from: '/blog/virtual_webhooks_vs_polling_jobs_how_integrations_handle_change_detection',
        to: '/blog/what_are_virtual_webhooks_how_they_differ_from_sync_based_webhooks',
    },

    // Retired, mistyped and moved marketing URLs still requested on unified.to (TICKET-9684).
    {
        from: '/advertising',
        to: '/ads',
    },
    {
        from: '/call-center',
        to: '/uc',
    },
    {
        from: '/docs',
        to: 'https://docs.unified.to/',
    },
    {
        from: '/docs/reference/sdks',
        to: 'https://docs.unified.to/reference/sdks',
    },
    {
        from: '/docs/reference/webhooks',
        to: 'https://docs.unified.to/reference/webhooks',
    },
    {
        from: '/e-commerce',
        to: '/commerce',
    },
    {
        from: '/guides',
        to: 'https://docs.unified.to/guides',
    },
    {
        from: '/help',
        to: '/blog',
    },
    {
        from: '/help/guides',
        to: 'https://docs.unified.to/guides',
    },
    {
        from: '/help/guides/passthrough_api_a_new_way_to_connect_with_your_integrations',
        to: 'https://docs.unified.to/passthrough/overview',
    },
    {
        from: '/help/passthrough_api_a_new_way_to_connect_with_your_vendors',
        to: 'https://docs.unified.to/passthrough/overview',
    },
    {
        from: '/help/typescript_sdk_client',
        to: 'https://docs.unified.to/reference/sdks',
    },
    {
        from: '/integrations/google-classroom',
        to: '/integrations/googleclassroom',
    },
    {
        from: '/integrations/greenhouseingest',
        to: '/integrations/greenhouse',
    },
    {
        from: '/integrations/oracletaleo',
        to: '/integrations/taleo',
    },
    {
        from: '/integrations/sage',
        to: '/accounting',
    },
    {
        from: '/integrations/updateai',
        to: '/calendar',
    },
    {
        from: '/repository',
        to: '/repo',
    },
    {
        from: '/sso',
        to: '/login',
    },

    // Old blog slugs still requested on unified.to (TICKET-9684).
    {
        from: '/blog/accounting_api_integration_invoices_bills_transactions_and_financial_reporting_across_platforms',
        to: '/blog/what_is_an_accounting_api_a_2026_guide_for_b2b_saas_engineering_teams',
    },
    {
        from: '/blog/api_pagination_why_it_breaks_multi_integration_systems_and_how_to_handle_it',
        to: '/blog/api_pagination_why_it_breaks_multi_integration_systems_and_how_to_fix_it',
    },
    {
        from: '/blog/apideck_vs_unified_two_pass_through_unified_apis_different_bets',
        to: '/blog/apideck_vs_unified_which_unified_api_is_right_for_your_product_in_2026',
    },
    {
        from: '/blog/apideck_vs_unified_which_pass_through_unified_api_is_right_for_your_product_in_2026',
        to: '/blog/apideck_vs_unified_which_unified_api_is_right_for_your_product_in_2026',
    },
    {
        from: '/blog/build_vs_buy_for_saas_integrations_why_a_unified_api_costs_less_to_maintain_than_building_in_house',
        to: '/blog/build_vs_buy_integrations_when_to_build_in_house_and_when_to_use_a_unified_api',
    },
    {
        from: '/blog/codat_vs_unified_financial_data_specialist_or_multi_category_unified_api',
        to: '/blog/codat_vs_unified_which_should_you_use_for_accounting_integrations_in_2026',
    },
    {
        from: '/blog/etl_vs_ipaas_vs_unified_api_which_integration_platform_requires_the_least_maintenance',
        to: '/blog/etl_vs_ipaas_vs_unified_api_complete_architecture_guide_for_saas_teams',
    },
    {
        from: '/blog/focus_on_what_matters_how_talin_ai_built_their_ai_platform_without_integration_headaches',
        to: '/blog/how_talin_ai_built_their_ai_platform_without_integration_headaches',
    },
    {
        from: '/blog/gmail_api_integration_a_step_by_step_guide_for_b2b_saas_teams_2026',
        to: '/blog/how_to_integrate_with_gmail_api_a_step_by_step_guide_for_developers',
    },
    {
        from: '/blog/how_to_build_a_rag_pipeline_for_a_qanda_bot_with_unified',
        to: '/blog/how_to_build_a_rag_pipeline_for_live_saas_data',
    },
    {
        from: '/blog/how_to_build_a_sales_lead_generation_product_with_unified',
        to: '/blog/how_to_push_qualified_leads_into_salesforce_hubspot_and_30_crms_with_a_unified_api',
    },
    {
        from: '/blog/how_to_check_if_an_oauth_token_is_expired',
        to: '/blog/oauth_token_expiry_how_to_check_if_a_token_is_expired',
    },
    {
        from: '/blog/how_to_integrate_with_the_salesforce_api_a_step_by_step_guide_for_b2b_saas_teams_2026',
        to: '/blog/salesforce_api_a_complete_guide_2026',
    },
    {
        from: '/blog/how_to_use_the_unified_file_storage_api',
        to: '/blog/file_storage_api_integration_real_time_files_hierarchies_and_secure_access_across_platforms',
    },
    {
        from: '/blog/how_we_normalize_oauth_across_460_apis_at_unified_to',
        to: '/blog/how_we_normalize_oauth_across_460_apis_at_unified',
    },
    {
        from: '/blog/how_we_normalize_oauth_across_apis_at_unified',
        to: '/blog/how_we_normalize_oauth_across_460_apis_at_unified',
    },
    {
        from: '/blog/hr_api_integration_real_time_employee_data_across_workforce_systems',
        to: '/blog/what_is_hr_api_integration_employee_org_and_payroll_data_across_hr_systems',
    },
    {
        from: '/blog/introducing_the_unified_file_storage_api',
        to: '/blog/file_storage_api_integration_real_time_files_hierarchies_and_secure_access_across_platforms',
    },
    {
        from: '/blog/introducing_unified_genai_api',
        to: '/blog/what_is_generative_ai_api_integration_models_prompts_embeddings_and_agents_across_providers',
    },
    {
        from: '/blog/introducing_unified_kms_api',
        to: '/blog/what_is_knowledge_management_api_integration_spaces_pages_comments_and_change_events_across_platforms',
    },
    {
        from: '/blog/introducing_unified_lms_api',
        to: '/blog/learning_management_system_lms_api_integration_real_time_course_data_and_learning_platforms',
    },
    {
        from: '/blog/introducing_unified_messaging_api',
        to: '/blog/unified_messaging_api_sync_email_and_chat_data_across_platform',
    },
    {
        from: '/blog/introducing_unified_repository_api',
        to: '/blog/unified_repository_api_access_code_repositories_across_github_gitlab_and_bitbucket',
    },
    {
        from: '/blog/introducing_unified_task_management_api',
        to: '/blog/unified_task_management_api_real_time_access_to_tasks_projects_and_work_state_across_platforms',
    },
    {
        from: '/blog/kombo_vs_unified_hr_specialist_or_multi_category_unified_api_in_2026',
        to: '/blog/kombo_vs_unified_sync_and_store_vs_live_source_reads_2026',
    },
    {
        from: '/blog/kombo_vs_unified_which_unified_api_is_better_for_ai_native_real_time_saas',
        to: '/blog/kombo_vs_unified_sync_and_store_vs_live_source_reads_2026',
    },
    {
        from: '/blog/paragon_vs_unified_which_unified_api_is_right_for_your_product',
        to: '/blog/paragon_vs_unified_embedded_ipaas_or_real_time_unified_api_in_2026',
    },
    {
        from: '/blog/pass_through_vs_sync_based_unified_apis_architecture_tradeoffs_explained',
        to: '/blog/pass_through_vs_sync_based_unified_apis_architecture_trade_offs_in_2026',
    },
    {
        from: '/blog/passthrough_api_a_new_way_to_connect_with_your_vendors',
        to: 'https://docs.unified.to/passthrough/overview',
    },
    {
        from: '/blog/top_merge_alternatives',
        to: '/blog/top_merge_dev_alternatives_in_2026_a_guide_for_saas_and_ai_teams',
    },
    {
        from: '/blog/understanding_native_and_virtual_webhooks',
        to: '/blog/what_are_virtual_webhooks_how_they_differ_from_sync_based_webhooks',
    },
    {
        from: '/blog/understanding_virtual_webhooks',
        to: '/blog/what_are_virtual_webhooks_how_they_differ_from_sync_based_webhooks',
    },
    {
        from: '/blog/unified_apis_vs_zapier',
        to: '/blog/unified_api_vs_zapier_which_do_you_actually_need',
    },
    {
        from: '/blog/what_is_idempotency_in_rest_api',
        to: '/blog/rest_api_integration_challenges_best_practices_and_what_breaks_at_scale',
    },
    {
        from: '/blog/what_is_payroll_integration_a_guide_for_b2b_saas_engineers_2026',
        to: '/blog/what_is_payroll_integration_a_complete_guide_2026',
    },
    {
        from: '/blog/workday_api_integration_what_engineers_need_to_know_before_you_build',
        to: '/blog/workday_api_integration_what_to_know_before_you_build',
    },

    // Blog consolidation pass (TICKET-11104): posts merged, retired or renamed on 12-21 Oct 2026, plus two old slugs.
    // Row 5 (hidden pass-through post) is not listed: its published slug is unknown and must be added once confirmed.
    {
        from: '/blog/why_zero_storage_unified_apis_simplify_compliance_for_saas_integrations',
        to: '/blog/why_unified_apis_shouldnt_store_your_customer_data_in_2026',
    },
    {
        from: '/blog/unified_security',
        to: '/blog/unified_api_security_how_zero_storage_architecture_protects_customer_data_in_real_time',
    },
    {
        from: '/blog/the_climate_of_ai_why_real_time_integration_data_infrastructure_matters',
        to: '/blog/ai_integration_use_cases_how_saas_teams_are_using_unified_for_rag_agents_and_vertical_ai',
    },
    {
        from: '/blog/unified_apis_vs_workflow_automation_which_should_developers_choose',
        to: '/blog/unified_api_vs_zapier_which_do_you_actually_need',
    },
    {
        from: '/blog/best_ai_agent_integration_platforms_to_consider_in_2026',
        to: '/blog/best_tools_for_giving_ai_agents_access_to_saas_apis',
    },
    {
        from: '/blog/why_use_a_unified_mcp_instead_of_connecting_directly_to_vendor_mcps',
        to: '/blog/best_mcp_servers_for_connecting_ai_agents_to_saas_apis',
    },
    {
        from: '/blog/api_solutions_for_real_time_event_sync_across_chat_storage_and_databases',
        to: '/blog/best_way_to_receive_updates_from_apis_without_webhooks_without_building_polling_systems',
    },
    {
        from: '/blog/building_integrations_yourself_vs_using_a_unified_api_a_developer_guide',
        to: '/blog/build_vs_buy_integrations_when_to_build_in_house_and_when_to_use_a_unified_api',
    },
    {
        from: '/blog/passthrough_api_a_new_way_to_connect_with_your_integrations',
        to: 'https://docs.unified.to/passthrough/overview',
    },
    {
        from: '/blog/how_to_use_the_passthrough_api',
        to: 'https://docs.unified.to/guides/how_to_use_the_passthrough_api',
    },
    {
        from: '/blog/building_your_integration_strategy_a_deep_dive_into_integration_technology',
        to: '/blog/etl_vs_ipaas_vs_unified_api_complete_architecture_guide_for_saas_teams',
    },
    {
        from: '/blog/the_hr_tech_transformation_leveraging_api_integrations_for_growth',
        to: '/blog/what_is_hr_api_integration_employee_org_and_payroll_data_across_hr_systems',
    },
    {
        from: '/blog/top_benefits_of_unified_apis_for_saas_integration',
        to: '/blog/what_is_a_unified_api',
    },
    {
        from: '/blog/unified_file_storage_api_integrations_guide',
        to: '/blog/file_storage_api_integration_real_time_files_hierarchies_and_secure_access_across_platforms',
    },
    {
        from: '/blog/unified_generative_ai_api_run_llms_with_real_time_data_and_actions_through_one_api',
        to: '/blog/what_is_generative_ai_api_integration_models_prompts_embeddings_and_agents_across_providers',
    },
    {
        from: '/blog/how_to_use_unified_generative_ai_api_with_openai_and_claude',
        to: 'https://docs.unified.to/guides/how_to_use_unified_generative_ai_api_with_openai_and_claude',
    },
    {
        from: '/blog/what_unified_api_is_best_for_connecting_saas_apps_to_llm_and_ai_model_apis',
        to: '/blog/what_is_generative_ai_api_integration_models_prompts_embeddings_and_agents_across_providers',
    },
    {
        from: '/blog/unified_kms_api_real_time_access_to_knowledge_base_and_documentation_data',
        to: '/blog/what_is_knowledge_management_api_integration_spaces_pages_comments_and_change_events_across_platforms',
    },
    {
        from: '/blog/how_to_build_ai_search_and_insights_across_google_drive_slack_zendesk_notion_and_460_platforms_with_a_unified_api',
        to: '/blog/how_to_build_ai_search_and_insights_across_google_drive_slack_zendesk_and_notion',
    },
    {
        from: '/blog/how_to_build_enterprise_search_across_google_drive_slack_notion_zendesk_and_other_platforms_with_a_unified_api',
        to: '/blog/how_to_build_ai_search_and_insights_across_google_drive_slack_zendesk_and_notion',
    },
    {
        from: '/blog/how_to_build_enterprise_knowledge_search_across_notion_google_drive_and_slack',
        to: '/blog/how_to_build_ai_search_and_insights_across_google_drive_slack_zendesk_and_notion',
    },
    {
        from: '/blog/what_unified_api_is_best_for_knowledge_base_integrations_like_notion_and_confluence',
        to: '/blog/knowledge_management_systems_unified_apis_features_use_cases_and_options',
    },
    {
        from: '/blog/unified_hr_api_access_payslip_and_time_off_data_across_hr_and_payroll_systems',
        to: '/blog/what_is_hr_api_integration_employee_org_and_payroll_data_across_hr_systems',
    },
    {
        from: '/blog/how_to_automate_employee_provisioning_across_workday_bamboohr_successfactors_and_180_hr_platforms_with_a_unified_api',
        to: '/blog/how_to_detect_employee_onboarding_and_offboarding_events_with',
    },
    {
        from: '/blog/how_to_sync_employee_and_user_data_from_workday_bamboohr_and_180_hr_platforms_with_a_unified_api',
        to: '/blog/how_to_build_org_charts_and_employee_directories_across_hris_platforms_with',
    },
    {
        from: '/blog/ats_activity_new_sub_type_field',
        to: '/blog/ats_api_integration_real_time_recruiting_data_candidate_pipelines_and_hiring_automation',
    },
    {
        from: '/blog/how_to_build_candidate_sourcing_across_greenhouse_lever_workday_and_60_ats_platforms_with_a_unified_api',
        to: '/blog/how_to_source_candidates_externally_and_add_them_to_an_ats_with',
    },
    {
        from: '/blog/integrate_the_indeed_api_with_unified',
        to: '/blog/how_to_build_a_job_board_integrating_greenhouse_lever_and_73_ats_platforms_with_an_ats_api',
    },
    {
        from: '/blog/how_to_build_a_candidate_sourcing_or_job_board_app_with_unified',
        to: 'https://docs.unified.to/guides/how_to_build_a_candidate_sourcing_or_job_board_app_with_unified',
    },
    {
        from: '/blog/how_to_build_interview_coordination_across_ats_and_calendar_platforms_with_ats_and_calendar_apis',
        to: '/blog/how_to_build_interview_scheduling_across_greenhouse_outlook_and_google_calendar_with',
    },
    {
        from: '/blog/how_to_build_candidate_assessments_across_workday_greenhouse_lever_and_60_ats_platforms_with_a_unified_api',
        to: '/blog/how_to_embed_assessments_in_ats_platforms_with',
    },
    {
        from: '/blog/unified_assessment_api_embed_assessments_inside_ats_platforms',
        to: '/blog/how_to_embed_assessments_in_ats_platforms_with',
    },
    {
        from: '/blog/knit_vs',
        to: '/blog/knit_vs_unified_which_unified_api_is_right_for_your_product_in_2026',
    },
    {
        from: '/blog/cli_command_line_interface_vs_mcp_model_context_protocol_architecture_tradeoffs_for_ai_agents_and_saas_applications',
        to: '/blog/cli_command_line_interface_vs_mcp_model_context_protocol_architecture_trade_offs_for_ai_agents',
    },

    // Targets above that were renamed after the ticket's 20-21 Oct check; their old slugs now 404.
    {
        from: '/blog/hr_api_integration_real_time_employee_data_directories_and_workforce_automation',
        to: '/blog/what_is_hr_api_integration_employee_org_and_payroll_data_across_hr_systems',
    },
    {
        from: '/blog/generative_ai_api_integration_multi_model_prompts_routing_and_embeddings_across_providers',
        to: '/blog/what_is_generative_ai_api_integration_models_prompts_embeddings_and_agents_across_providers',
    },
    {
        from: '/blog/knowledge_management_api_integration_spaces_pages_comments_and_real_time_knowledge_content_across_platforms',
        to: '/blog/what_is_knowledge_management_api_integration_spaces_pages_comments_and_change_events_across_platforms',
    },
    {
        from: '/blog/how_to_sync_employee_lifecycle_events_onboarding_and_offboarding_with_unified_hr_and_directory_api',
        to: '/blog/how_to_detect_employee_onboarding_and_offboarding_events_with',
    },
    {
        from: '/blog/how_to_build_org_charts_and_employee_directories_with_unified_hr_and_directory_api',
        to: '/blog/how_to_build_org_charts_and_employee_directories_across_hris_platforms_with',
    },
    {
        from: '/blog/how_to_source_candidates_externally_and_push_them_into_an_ats_using_unified_ats_api',
        to: '/blog/how_to_source_candidates_externally_and_add_them_to_an_ats_with',
    },
    {
        from: '/blog/how_to_build_interview_scheduling_across_greenhouse_outlook_and_google_calendar_with_ats_and_calendar_apis',
        to: '/blog/how_to_build_interview_scheduling_across_greenhouse_outlook_and_google_calendar_with',
    },
    {
        from: '/blog/how_to_offer_assessment_packages_and_receive_orders_with_unified_assessment_api',
        to: '/blog/how_to_embed_assessments_in_ats_platforms_with',
    },
    {
        from: '/blog/how_to_build_a_candidate_assessment_product_with_unified',
        to: '/blog/how_to_embed_assessments_in_ats_platforms_with',
    },

    // Retired virtual webhooks post that the blog auto-link pointed at.
    {
        from: '/blog/unlock_real_time_data_with_virtual_webhooks',
        to: '/blog/what_are_virtual_webhooks_how_they_differ_from_sync_based_webhooks',
    },

    // Retired marketing pages.
    { from: '/vs/merge', to: MERGE_VS_UNIFIED },
    { from: '/saml', to: '/login' },
    { from: '/passthrough', to: 'https://docs.unified.to/passthrough/overview' },
    { from: '/metadata', to: 'https://docs.unified.to/metadata/overview' },

    // Renamed integration / concept pages.
    { from: '/integrations/zenefits', to: '/integrations/trinet' },
    { from: '/integration-authorization', to: 'https://docs.unified.to/concepts/embedded-components' },

    // Old in-site docs paths (the docs now live on docs.unified.to).
    // pages/apidocs.vue only redirects in the browser after the page loads; crawlers get a 404.
    { from: '/apidocs', to: 'https://docs.unified.to/' },
    // docs.unified.to/intro returns a 404 status; the docs home is the introduction
    { from: '/docs/intro', to: 'https://docs.unified.to/' },
    { from: '/docs/reference/pagination', to: 'https://docs.unified.to/reference/pagination' },
    { from: '/docs/reference/rate_limits', to: 'https://docs.unified.to/reference/rate_limits' },
];

/** Pages that are removed outright (real 404, no redirect): /startup and /promo. */
export const REMOVED_PAGES: readonly string[] = ['/startup', '/promo'];

/** Pages that stay live but must not be indexed or listed in the sitemap. */
export const NOINDEX_PAGES: readonly string[] = ['/thanks'];

/**
 * Integrations that must not appear anywhere on the website (listing, detail page, sitemap, llms.txt,
 * blog links). `greenhouseingest` is internal; `gusto` is not offered publicly (TICKET-10387).
 */
export const HIDDEN_INTEGRATION_TYPES: readonly string[] = ['greenhouseingest', 'gusto'];

/** Competitor comparison pages that are retired (redirected above, excluded from prerender + sitemap). */
export const RETIRED_COMPETITOR_SLUGS: readonly string[] = ['merge'];

/** Category pages that are retired (redirected above, excluded from prerender + sitemap). */
export const RETIRED_CATEGORY_SLUGS: readonly string[] = ['saml'];

/** Routes that must not appear in sitemap.xml. */
export const SITEMAP_EXCLUDE: readonly string[] = [
    ...SITE_REDIRECTS.map((r) => r.from).filter((p) => !p.startsWith('/blog/') && !p.startsWith('/docs/')),
    ...REMOVED_PAGES,
    ...NOINDEX_PAGES,
];

/** Redirected blog slugs; the dynamic sitemap source must not list them (TICKET-11152). */
export const REDIRECTED_BLOG_PATHS: ReadonlySet<string> = new Set(SITE_REDIRECTS.map((r) => r.from).filter((p) => p.startsWith('/blog/')));
